import {
  type CertificadoAcessosDto,
  mapCertificadoAcessos,
  type PartePainelComprovacao,
  type VarianteStatusCertificado,
} from '@/features/arbitral-detalhes';
import { formatDateTimeShort } from '@/shared/formatters';

import type { CertificadoAcessosSentenca } from '../types/certificadoAcessosSentenca.types';
import type { CertificadoAcessosSentencaDto } from '../types/certificadoAcessosSentencaDto.schema';

type ComprovacaoSentenca = CertificadoAcessosSentencaDto['comprovacao'][number];
type PontoDaComprovacao = ComprovacaoSentenca['ponto'];

/**
 * O que a linha da parte diz no painel de comprovação.
 *
 * A ENTREGA é o degrau que permite avançar (rn3), então é ela que o rótulo nomeia quando
 * alcançada — inclusive quando a parte já leu, porque "Lido" sem a palavra entregue deixaria a
 * Secretaria procurando a comprovação que já existe. Nenhum rótulo aqui pode sugerir que falta
 * a pessoa ler: `LIDO` é informação adicional sobre o que houve DEPOIS da entrega.
 */
const ROTULO_DO_PONTO: Record<PontoDaComprovacao, string> = {
  NAO_ENVIADO: 'Sem comunicação enviada',
  ENVIADO: 'Enviado, entrega não confirmada',
  ENTREGUE: 'Entrega comprovada',
  LIDO: 'Entrega comprovada e lida',
};

// Só a entrega comprovada é `success`: é o marco que destrava o encerramento. `ENVIADO` fica em
// `warning` porque é pendência real para a Secretaria — não é falha, mas também não avança.
const VARIANTE_DO_PONTO: Record<PontoDaComprovacao, VarianteStatusCertificado> = {
  NAO_ENVIADO: 'neutral',
  ENVIADO: 'warning',
  ENTREGUE: 'success',
  LIDO: 'success',
};

function situacaoLabel(parte: ComprovacaoSentenca): string {
  const rotulo = ROTULO_DO_PONTO[parte.ponto];
  if (!parte.ultimoMarcoEm) return rotulo;
  return `${rotulo} · ${formatDateTimeShort(parte.ultimoMarcoEm)}`;
}

/**
 * Adapta o DTO da sentença ao contrato do certificado do processo para atravessar o MESMO mapper.
 *
 * `correios: []` e a comprovação sem os campos próprios da sentença: o mapper compartilhado só
 * lê o que os dois documentos têm em comum, e a seção postal não é renderizada por este
 * documento — então lista vazia aqui é ausência de dado, não seção vazia na tela (rn6).
 */
function comoCertificadoDoProcesso(dto: CertificadoAcessosSentencaDto): CertificadoAcessosDto {
  return {
    ...dto,
    correios: [],
    comprovacao: dto.comprovacao.map((parte) => ({
      parteArbitralId: parte.parteArbitralId,
      nome: parte.nome,
      documento: parte.documento,
      situacao: parte.situacao,
      canais: parte.canais,
    })),
  };
}

/**
 * O painel de comprovação é o mesmo componente dos dois certificados, então o destaque da
 * ENTREGA entra pelo DADO, no rótulo que a linha da parte já exibe — não por elemento novo na
 * tela. É o que permite corrigir o painel uma vez e valer nos dois documentos (rnf3).
 */
function comDestaqueDaEntrega(
  partesDoPainel: PartePainelComprovacao[],
  comprovacao: ComprovacaoSentenca[],
): PartePainelComprovacao[] {
  // O índice é o vínculo porque `montarResumo` mapeia `dto.comprovacao` 1:1 e na ordem, e o
  // view-model do painel não carrega o id da parte. Se um dos lados passar a filtrar, o degrau
  // sai da parte errada — daí o guard, que prefere o rótulo do mapper compartilhado a um errado.
  return partesDoPainel.map((parte, indice) => {
    const daSentenca = comprovacao[indice];
    if (!daSentenca || daSentenca.nome !== parte.nome) return parte;
    return {
      ...parte,
      situacaoLabel: situacaoLabel(daSentenca),
      situacaoVariante: VARIANTE_DO_PONTO[daSentenca.ponto],
    };
  });
}

export function mapCertificadoAcessosSentenca(
  dto: CertificadoAcessosSentencaDto,
): CertificadoAcessosSentenca {
  const base = mapCertificadoAcessos(comoCertificadoDoProcesso(dto));
  return {
    processoId: base.processoId,
    numeroProcesso: base.numeroProcesso,
    dataEmissao: base.dataEmissao,
    sentencaProferidaEm: dto.sentenca.proferidaEm,
    intimacaoEnviadaEm: dto.sentenca.intimacaoEnviadaEm,
    codigoAutenticacao: base.codigoAutenticacao,
    urlAutenticacao: base.urlAutenticacao,
    urlAutenticacaoExibicao: base.urlAutenticacaoExibicao,
    hashCertificado: base.hashCertificado,
    versaoHash: base.versaoHash,
    cadeiaIntegra: base.cadeiaIntegra,
    cadeiaQuebradaNaVersao: base.cadeiaQuebradaNaVersao,
    cadeiaDasComunicacoes: dto.cadeiaDasComunicacoes
      ? {
          hashTopo: dto.cadeiaDasComunicacoes.hashTopo,
          versaoTopo: dto.cadeiaDasComunicacoes.versaoTopo,
          integra: dto.cadeiaDasComunicacoes.integra,
          quebradaNaVersao: dto.cadeiaDasComunicacoes.quebradaNaVersao,
        }
      : null,
    gruposPartes: base.gruposPartes,
    objetivoParagrafos: base.objetivoParagrafos,
    textosSeguranca: base.textosSeguranca,
    resumoComprovacao: {
      ...base.resumoComprovacao,
      partes: comDestaqueDaEntrega(base.resumoComprovacao.partes, dto.comprovacao),
    },
    canaisAuditoria: base.canaisAuditoria,
    acessosPlataforma: base.acessosPlataforma,
    mensagensWhatsapp: base.mensagensWhatsapp,
    mensagensEmail: base.mensagensEmail,
    instituicao: base.instituicao,
  };
}
